import {observer} from 'mobx-react';
import React from 'react';

import analytics from 'analytics.js';
import autobind from 'common/decorators/autobind.js';
import Avatar from 'core/components/Avatar.js';
import BookNowButton from 'core/components/BookNowButton.js';
import CallNowButton from 'core/components/CallNowButton.js';
import Chips from 'core/components/Chips.js';

import {
    BP_FHIR,
    HOSPITAL_CONTACT_TYPE,
    PRACTICE_GRP_CONTACT_TYPE,
    PRACTICE_LOC_CONTACT_TYPE,
    BOOK_NOW_BTN_REQ_SECONDARY,
    TELEHEALTH,
    ZM,
    SHOW_REFERRAL_BTN,
} from 'core/constants.js';
import Info from 'core/components/Info.js';
import LinkWithProperties from 'core/components/LinkWithProperties.js';
import {PracticeStoreContext} from 'core/stores/RootStore.js';
import {TELEHEALTH_ONLY, TELEHEALTH_HYBRID} from 'core/telehealth.js';
import Tabs from 'practices/components/Tabs.js';
import {
    BPSecureMessageButton,
    BPWriteReferralButton,
    HSReferralButton,
    LBWriteReferralButton,
    MDWriteReferralButton,
    ZMWriteReferralButton,
} from 'professional/components/ReferralButtons.js';
import ButtonGroup, {BUTTON_TYPE} from 'core/components/ButtonGroup.js';
import Link from 'core/components/Link';
import Button from 'core/components/Button';
import Dropdown from 'core/components/Dropdown';
import {openLinkInNewTab} from 'core/utils.js';
import {capitalizeString} from 'utils.js';
import {sessionStorageGetItem} from 'core/utils.js';
export default
@observer
class PracticeHeader extends React.Component {
    static contextType = PracticeStoreContext;
    constructor(props) {
        super(props);

        this.headerRef = React.createRef();
        this.minHeight = 160;

        this.state = {
            aboveMinHeight: false,
        };
    }

    componentDidUpdate() {
        const {height} = this.headerRef.current.getBoundingClientRect();
        if (height > this.minHeight && !this.state.aboveMinHeight) {
            this.setState({aboveMinHeight: true});
        }
    }

    getLinkText(amountOfLocations) {
        if (amountOfLocations === 2) {
            return '1 other location';
        }
        return `${amountOfLocations - 1} other locations`;
    }

    renderAddress() {
        const {
            header: {
                amountOfLocations,
                streetAddress,
                practiceGroupLink,
                locality,
            },
            practice,
            rootStore: {
                healthFundStore: {healthFund},
            },
        } = this.context;

        if (
            streetAddress ||
            (practiceGroupLink && amountOfLocations) ||
            locality
        ) {
            return (
                <h2>
                    {practice?.offersTelehealth !== TELEHEALTH_ONLY && (
                        <>
                            {streetAddress}
                            {streetAddress && locality && <br />}
                            {locality}
                        </>
                    )}
                    {practiceGroupLink && amountOfLocations > 1 && (
                        <>
                            {practice?.offersTelehealth !== TELEHEALTH_ONLY &&
                                ' — '}
                            <LinkWithProperties
                                href={practiceGroupLink}
                                searchParams={
                                    healthFund?.id
                                        ? {'health_fund': healthFund.id}
                                        : undefined
                                }
                            >
                                {this.getLinkText(amountOfLocations)}
                            </LinkWithProperties>
                        </>
                    )}
                </h2>
            );
        } else {
            return null;
        }
    }
    renderDropOptions(practice) {
        const {showAppointmentModal, isPracticeGroup} = this.context;
        const {practitionersWithEmail} = practice;

        return practitionersWithEmail.map((selectedPractitioner) => {
            const {
                avatar,
                displayName,
                address,
                initials,
                specialties,
            } = selectedPractitioner;
            const renderSpecialties = () => {
                return (
                    <span>{specialties.map((sp) => sp.name).join(', ')}</span>
                );
            };
            return {
                name: displayName,
                content: (
                    <Button
                        action={() => {
                            showAppointmentModal({
                                ...practice,
                                practitioners: selectedPractitioner,
                            });
                        }}
                        text={
                            <>
                                <Avatar
                                    avatar={avatar}
                                    displayName={displayName}
                                    initials={initials}
                                />
                                <section>
                                    <h3>{displayName}</h3>
                                    {isPracticeGroup && renderSpecialties()}
                                    {!isPracticeGroup && (
                                        <address>{address}</address>
                                    )}
                                </section>
                            </>
                        }
                    />
                ),
            };
        });
    }

    getButtons() {
        const {
            isHospitalPage,
            isPracticeGroup,
            contactProps,
            practice,
            showAppointmentModal,
            rootStore: {
                paramStore: {hasSkin, isReferrals, isClient},
            },
            practice: {zmData},
        } = this.context;

        const {referralToPractice} = practice;
        const buttons = [];
        let practitionerArray = {};
        const showReferralBtn =
            sessionStorageGetItem(SHOW_REFERRAL_BTN) === 'true';

        if (!hasSkin) {
            // Do not show any "Book Now" button in skinned version
            if (practice.bookingsEnabled?.length > 0) {
                buttons.push({
                    buttonType: BUTTON_TYPE.BOOK_NOW,
                    component: (
                        <BookNowButton
                            objectType={
                                isPracticeGroup
                                    ? 'practice group'
                                    : 'practice location'
                            }
                            objectId={practice.id}
                            params={
                                isPracticeGroup
                                    ? {'practice_group': practice.id}
                                    : {'practice_location': practice.id}
                            }
                        />
                    ),
                });
            } else {
                if (isPracticeGroup) {
                    practitionerArray = {locations: practice.locations};

                    practitionerArray.practitionersWithEmail = practice.practitioners?.filter(
                        (practitioner) =>
                            practitioner.locations?.some(
                                (location) => location.requestAppointmentEmail,
                            ),
                    );
                } else {
                    const practitionersWithEmail = practice.practitioners?.filter(
                        (p) => p.requestAppointmentEmail,
                    );

                    practitionerArray = {
                        ...practice,
                        practitionersWithEmail,
                    };
                }

                if (practice.name?.toUpperCase() === 'EVOLVEMED') {
                    buttons.push({
                        buttonType: BUTTON_TYPE.INTEGRATED_BOOK_NOW,
                        component: (
                            <Link
                                customClass="cta"
                                href={
                                    'https://www.hotdoc.com.au/medical-centres/little-bay-NSW-2036/evolvemed/doctors'
                                }
                                isExternalLink={true}
                                rel={'nofollow'}
                            >
                                {'Book now'}
                            </Link>
                        ),
                    });
                }
                if (practice.id === 410 && !isPracticeGroup) {
                    buttons.push({
                        buttonType: BUTTON_TYPE.INTEGRATED_BOOK_NOW,
                        component: (
                            <Link
                                customClass="cta"
                                href={
                                    'https://portal.coreplus.com.au/psychologistsouthyarra'
                                }
                                isExternalLink={true}
                                rel={'nofollow'}
                            >
                                {'Book now'}
                            </Link>
                        ),
                    });
                }
                if (practice.id === 34942 && !isPracticeGroup) {
                    buttons.push({
                        buttonType: BUTTON_TYPE.INTEGRATED_BOOK_NOW,
                        component: (
                            <Link
                                customClass="cta"
                                href={
                                    'https://accesstelehealth.com.au/telehealth-specialists-australia-access-specialist/i-am-a-patient/connecting-you-to-specialist-care-access-telehealth/'
                                }
                                isExternalLink={true}
                                rel={'nofollow'}
                            >
                                {'Book now'}
                            </Link>
                        ),
                    });
                }

                if (practitionerArray.practitionersWithEmail) {
                    const {practitionersWithEmail} = practitionerArray;
                    const {
                        locality,
                        localityId,
                        postCode,
                        name,
                        phones,
                    } = practitionerArray;
                    const modalLocation = {
                        locality,
                        localityId,
                        postCode,
                        name,
                        phones,
                    };
                    if (practitionersWithEmail.length === 1) {
                        buttons.push({
                            buttonType: BUTTON_TYPE.APPOINTMENT,
                            component: (
                                <Button
                                    action={() =>
                                        showAppointmentModal({
                                            ...modalLocation,
                                            practitioners:
                                                practitionersWithEmail[0],
                                        })
                                    }
                                    customClass="cta 1stAvailableWidget2"
                                    text={'Book now'}
                                />
                            ),
                        });
                    } else if (practitionersWithEmail.length > 1) {
                        buttons.push({
                            buttonType: BUTTON_TYPE.APPOINTMENT,
                            component: (
                                <Dropdown
                                    customClass="cta expand-cta"
                                    gaSelector={BOOK_NOW_BTN_REQ_SECONDARY}
                                    displayText="Book now"
                                    dropdownHeading={
                                        <h3>Choose a Practitioner</h3>
                                    }
                                    options={this.renderDropOptions({
                                        ...modalLocation,
                                        practitionersWithEmail,
                                    })}
                                    optionsClassName={'practitioner'}
                                />
                            ),
                        });
                    }
                }
                if (buttons.length === 0 && practice.customBookingDataUrl) {
                    buttons.push({
                        buttonType: BUTTON_TYPE.INTEGRATED_BOOK_NOW,
                        component: (
                            <BookNowButton
                                action={() =>
                                    openLinkInNewTab(
                                        practice.customBookingDataUrl,
                                    )
                                }
                                objectType={
                                    isPracticeGroup
                                        ? 'practice group'
                                        : 'practice location'
                                }
                                objectId={practice.id}
                                params={
                                    isPracticeGroup
                                        ? {'practice_group': practice.id}
                                        : {'practice_location': practice.id}
                                }
                            />
                        ),
                    });
                }
            }
        }

        if (contactProps && !isClient) {
            let contactType;
            if (isHospitalPage) {
                contactType = HOSPITAL_CONTACT_TYPE;
            }
            if (isPracticeGroup) {
                contactType = PRACTICE_GRP_CONTACT_TYPE;
            } else if (!isPracticeGroup && !isHospitalPage) {
                contactType = PRACTICE_LOC_CONTACT_TYPE;
            }
            buttons.push({
                buttonType: BUTTON_TYPE.CALL_NOW,
                component: <CallNowButton contactType={contactType} />,
            });
        }

        if (
            (zmData && !showReferralBtn) ||
            (isReferrals && referralToPractice)
        ) {
            buttons.push({
                buttonType: BUTTON_TYPE.REFERRAL,
                component: this.renderHsReferralButton(),
            });
        }

        return buttons;
    }

    getReferralData(data) {
        const {offersTelehealth} = this.context.practice;
        const referralData = {...data};
        if (offersTelehealth === TELEHEALTH_ONLY) {
            // remove address data if pure telehealth
            // bp data
            delete referralData.AddressLine1;
            delete referralData.AddressLine2;
            delete referralData.Postcode;
            referralData.Suburb = 'Telehealth';

            //md data
            delete referralData.City;
            delete referralData.PCity;
            delete referralData.PPostcode;
            delete referralData.PStreetLine1;
            delete referralData.PStreetLine2;
            delete referralData.PStreetLine3;
            delete referralData.StreetLine1;
            delete referralData.StreetLine2;
            delete referralData.StreetLine3;
        }
        return referralData;
    }

    getReferralButtons() {
        const {
            rootStore: {
                paramStore: {client},
            },
            practice: {bpData, id, mdData, zmData, lbData},
        } = this.context;
        const buttons = [];
        const showReferralBtn =
            sessionStorageGetItem(SHOW_REFERRAL_BTN) === 'true';
        if (bpData) {
            buttons.push({
                buttonType: BUTTON_TYPE.REFERRAL,
                component: (
                    <BPWriteReferralButton
                        bpData={this.getReferralData(bpData)}
                        eventData={{
                            'practice_location_id': id,
                        }}
                    />
                ),
            });
        }

        if (mdData) {
            buttons.push({
                buttonType: BUTTON_TYPE.REFERRAL,
                component: (
                    <MDWriteReferralButton
                        eventData={{
                            'practice_location_id': id,
                        }}
                        mdData={this.getReferralData(mdData)}
                    />
                ),
            });
        }

        if (bpData && client === BP_FHIR) {
            buttons.push({
                buttonType: BUTTON_TYPE.SECURE_MESSAGE,
                component: (
                    <BPSecureMessageButton
                        eventData={{
                            'practice_location_id': id,
                        }}
                        fhirData={this.getReferralData(bpData)}
                    />
                ),
            });
        }

        if (zmData && client == ZM && showReferralBtn) {
            buttons.push({
                buttonType: BUTTON_TYPE.REFERRAL,
                component: (
                    <ZMWriteReferralButton
                        eventData={{'practice_location_id': id}}
                        zmData={zmData}
                    />
                ),
            });
        }
        if (lbData) {
            buttons.push({
                buttonType: BUTTON_TYPE.REFERRAL,
                component: (
                    <LBWriteReferralButton
                        lbData={lbData}
                        eventData={{
                            'practice_location_id': id,
                        }}
                    />
                ),
            });
        }


        return buttons;
    }

    renderHsReferralButton() {
        const {
            getCopyData,
            practice: {id},
        } = this.context;
        return (
            <HSReferralButton
                eventData={{
                    eventName: 'copyPracticeLocationDetails',
                    data: {
                        'practice_location': id,
                    },
                }}
                getCopyData={getCopyData}
            />
        );
    }

    @autobind
    onClickFaxNumber(phoneNumber) {
        const {
            isPracticeGroup,
            isHospitalPage,
            practice: {id},
        } = this.context;
        const trackData = {phoneNumber};
        if (isPracticeGroup) {
            trackData.practiceGroupId = id;
            analytics.track('practiceGroupFaxClick', trackData);
        } else if (!isHospitalPage) {
            trackData.practiceLocationId = id;
            analytics.track('practiceLocationFaxClick', trackData);
        }
    }

    get offersTelehealth() {
        if (this.context.isPracticeGroup) {
            const telehealthPractices = this.context.practice.locations?.filter(
                (p) =>
                    p.offersTelehealth === TELEHEALTH_ONLY ||
                    p.offersTelehealth === TELEHEALTH_HYBRID,
            );
            return telehealthPractices?.length > 0;
        }
        return (
            this.context.practice.offersTelehealth === TELEHEALTH_ONLY ||
            this.context.practice.offersTelehealth === TELEHEALTH_HYBRID
        );
    }

    renderGroup() {
        const {group, type} = this.context.header;
        const typeString = capitalizeString(type);
        if (group && typeString) {
            return (
                <h2>
                    {typeString} Hospital • {group}
                </h2>
            );
        } else if (typeString) {
            return <h2>{typeString} Hospital</h2>;
        } else if (group) {
            return <h2>{group}</h2>;
        }
    }

    renderHeader() {
        const {
            languagesSpoken,
            address,
            avatar,
            fax,
            healthFundRelationship,
            phones,
            name,
            website,
        } = this.context.header;
        const {isHospitalPage} = this.context;
        return (
            <div>
                {avatar && <Avatar avatar={avatar} displayName={name} />}
                <h1>{name}</h1>
                {isHospitalPage && this.renderGroup()}
                {this.renderAddress()}
                <Info
                    address={isHospitalPage && address ? address : undefined}
                    fax={fax?.number ? fax : undefined}
                    languages={languagesSpoken}
                    onClickFaxNumber={this.onClickFaxNumber}
                    onShowPhoneNumber={(phoneNumber) =>
                        this.context.showModal('contact', {phoneNumber})
                    }
                    phones={phones?.numbers.length > 0 ? phones : undefined}
                    website={website?.href ? website : undefined}
                >
                    {this.offersTelehealth && (
                        <Chips
                            customClass="telehealth"
                            labels={[
                                {
                                    customClass: 'telehealth',
                                    name: TELEHEALTH,
                                    tooltip:
                                        'This practitioner offers phone and/or video consultations.',
                                },
                            ]}
                        />
                    )}

                    {isHospitalPage && healthFundRelationship && (
                        <Chips
                            customClass={healthFundRelationship.name
                                ?.toLowerCase()
                                .replace(/[^a-zA-Z]/g, '')}
                            labels={[
                                {
                                    name: healthFundRelationship.displayName,
                                    tooltip: healthFundRelationship.tooltip,
                                },
                            ]}
                        />
                    )}
                </Info>
            </div>
        );
    }

    renderButtonGroup() {
        const {
            practice: {bpData, mdData, zmData, lbData},
        } = this.context;
        const showReferralBtn =
            sessionStorageGetItem(SHOW_REFERRAL_BTN) === 'true';
        const buttons =
            bpData || mdData || (zmData && showReferralBtn) || lbData
                ? this.getReferralButtons()
                : this.getButtons();
        return <ButtonGroup buttons={buttons} />;
    }

    render() {
        const {activeTab, practiceLoaded} = this.context;
        if (!practiceLoaded) {
            return null;
        }
        let className = 'card-profile';
        if (!this.state.aboveMinHeight) {
            className += ' collapsed';
        }
        return (
            <header className={className} ref={this.headerRef}>
                <div>
                    {this.renderHeader()}
                    <div className="button-group">
                        {this.renderButtonGroup()}
                    </div>
                </div>
                {activeTab !== null && <Tabs parentRef={this.headerRef} />}
            </header>
        );
    }
}
